/** Shared limits keep editable imports below the collaboration record budget. */
export const MAX_TABLE_ROWS = 200;
export const MAX_TABLE_COLUMNS = 20;
export const MAX_CONTENT_BYTES = 80_000;
export const contentBytes = (value: unknown) =>
  new TextEncoder().encode(JSON.stringify(value)).length;
export function checkedRows(rows: string[][]) {
  if (!rows.length || !rows.some((r) => r.length))
    throw new Error("This worksheet is empty.");
  if (
    rows.length > MAX_TABLE_ROWS ||
    rows.some((r) => r.length > MAX_TABLE_COLUMNS)
  )
    throw new Error(
      "Editable tables support up to 200 rows and 20 columns. Choose a file preview for a larger worksheet.",
    );
  if (
    contentBytes(rows) > MAX_CONTENT_BYTES ||
    rows.some((r) => r.some((c) => c.length > 4000))
  )
    throw new Error(
      "This table has too much text to edit on the board. Choose a file preview.",
    );
  const width = Math.max(...rows.map((r) => r.length));
  return rows.map((r) => Array.from({ length: width }, (_, c) => r[c] ?? ""));
}
export function parseDelimited(text: string, delimiter = ",") {
  const rows: string[][] = [];
  let row: string[] = [],
    value = "",
    quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      if (quoted && text[i + 1] === '"') {
        value += '"';
        i++;
      } else if (quoted || !value) quoted = !quoted;
      else value += c;
    } else if (!quoted && (c === delimiter || c === "\n" || c === "\r")) {
      row.push(value);
      value = "";
      if (c !== delimiter) {
        rows.push(row);
        row = [];
        if (c === "\r" && text[i + 1] === "\n") i++;
      }
    } else value += c;
  }
  if (quoted)
    throw new Error(
      "An opening quote in this spreadsheet has no closing quote.",
    );
  if (value || row.length) {
    row.push(value);
    rows.push(row);
  }
  return rows;
}
export function pageRange(input: string, count: number) {
  if (!Number.isInteger(count) || count < 1)
    throw new Error("Page count unavailable.");
  const value = input.trim().toLowerCase();
  const pages = new Set<number>();
  const add = (a: number, b: number) => {
    if (a < 1 || b > count || b < a || b - a >= 50)
      throw new Error(`Choose pages from 1 to ${count}, up to 50 at a time.`);
    for (let n = a; n <= b; n++) {
      pages.add(n);
      if (pages.size > 50) throw new Error("Extract up to 50 pages at a time.");
    }
  };
  if (value === "all") add(1, count);
  else
    for (const part of value.split(",")) {
      const match = /^(\d+)(?:\s*-\s*(\d+))?$/.exec(part.trim());
      if (!match) throw new Error("Use page numbers such as 1, 3-5, or all.");
      add(Number(match[1]), Number(match[2] || match[1]));
    }
  return [...pages];
}
export function sortedRows(
  rows: string[][],
  column: number,
  ascending: boolean,
  headers: boolean,
) {
  const start = headers ? 1 : 0;
  const compare = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return [
    ...rows.slice(0, start),
    ...rows
      .slice(start)
      .sort(
        (a, b) =>
          compare.compare(a[column] || "", b[column] || "") *
          (ascending ? 1 : -1),
      ),
  ];
}
